import { useParams } from 'react-router-dom';
import { getPublished } from '@/data/loader';
import { useI18n } from '@/hooks/useI18n';
import { useTranslation } from '@/hooks/useTranslation';
import { EntityCard } from '@/components/EntityCard';
import { CategorySEO } from '@/components/SEO';
import { Container } from '@/components/Container';
import { OptimizedImage } from '@/components/OptimizedImage';
import { NotFoundPage } from './NotFoundPage';

const categoryConfig: Record<string, { entityType: string; image: string }> = {
  praia: { entityType: 'praia', image: '/images/hero-bg.jpg' },
  praias: { entityType: 'praia', image: '/images/hero-bg.jpg' },
  trilha: { entityType: 'trilha', image: '/images/trilha-floresta.jpg' },
  trilhas: { entityType: 'trilha', image: '/images/trilha-floresta.jpg' },
  atracao: { entityType: 'atracao', image: '/images/farol-card.jpg' },
  atracoes: { entityType: 'atracao', image: '/images/farol-card.jpg' },
  passeio: { entityType: 'passeio', image: '/images/sailboat.jpg' },
  passeios: { entityType: 'passeio', image: '/images/sailboat.jpg' },
  pousada: { entityType: 'pousada', image: '/images/sunset-beach.jpg' },
  pousadas: { entityType: 'pousada', image: '/images/sunset-beach.jpg' },
  restaurante: { entityType: 'restaurante', image: '/images/praia-do-farol-hero.jpg' },
  restaurantes: { entityType: 'restaurante', image: '/images/praia-do-farol-hero.jpg' },
  morro: { entityType: 'morro', image: '/images/trilha-floresta.jpg' },
  morros: { entityType: 'morro', image: '/images/trilha-floresta.jpg' },
  vila: { entityType: 'vila', image: '/images/hero-bg.jpg' },
  vilas: { entityType: 'vila', image: '/images/hero-bg.jpg' },
  ilha: { entityType: 'ilha', image: '/images/sailboat.jpg' },
  ilhas: { entityType: 'ilha', image: '/images/sailboat.jpg' },
  trapiche: { entityType: 'trapiche', image: '/images/praia-do-farol-hero.jpg' },
  trapiches: { entityType: 'trapiche', image: '/images/praia-do-farol-hero.jpg' },
};

interface CategoryListPageProps {
  category?: string;
  canonicalPath?: string;
}

export function CategoryListPage({
  category: categoryProp,
  canonicalPath,
}: CategoryListPageProps = {}) {
  const { category: categoryParam } = useParams<{ category: string }>();
  const category = categoryProp || categoryParam;
  const { lang } = useI18n();
  const { t } = useTranslation();
  const config = categoryConfig[category || ''];

  if (!config) {
    return <NotFoundPage />;
  }

  const entities = getPublished(config.entityType, lang);
  const partners = entities.filter((e: any) => e.provider?.commission?.length > 0);
  const regular = entities.filter((e: any) => !e.provider?.commission);

  const title = t(`category.${category}.title`);
  const subtitle = t(`category.${category}.subtitle`);

  return (
    <>
      <CategorySEO category={category || ''} canonicalPath={canonicalPath} />
      <div>
        <div className="relative mb-10">
          <div className="relative h-48 sm:h-56">
            <OptimizedImage src={config.image} alt={title} className="w-full h-full object-cover" priority />
            <div className="absolute inset-0 bg-gradient-to-t from-sand-100/95 via-sand-100/40 to-transparent" />
          </div>
          <div className="absolute bottom-0 left-0 right-0 pb-6">
            <Container>
              <h1 className="font-display text-4xl sm:text-5xl text-warm-dark">{title}</h1>
              <p className="font-editorial text-lg text-warm-gray mt-1 max-w-xl">{subtitle}</p>
              <div className="mt-3 inline-flex items-center gap-2 bg-white rounded-full px-4 py-1.5 shadow-sm border border-sand-200">
                <span className="font-display text-xl text-teal-deep">{entities.length}</span>
                <span className="font-body text-sm text-warm-gray">{t('category.items')}</span>
              </div>
            </Container>
          </div>
        </div>

        <Container>
          {partners.length > 0 && (
            <div className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-coral/15 text-coral font-body text-xs font-bold px-3 py-1 rounded-full border border-coral/20">
                  {t('category.partnerBadge')}
                </span>
                <span className="font-body text-sm text-warm-gray">
                  {t('category.partnerSubtitle')}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {partners.map((entity: any) => (
                  <EntityCard key={entity.handle} entity={entity} category={category || ''} />
                ))}
              </div>
            </div>
          )}

          {regular.length > 0 && (
            <div>
              {partners.length > 0 && (
                <div className="flex items-center gap-2 mb-4">
                  <span className="bg-teal-light/10 text-teal-deep font-body text-xs font-bold px-3 py-1 rounded-full border border-teal-light/20">
                    {t('category.guideBadge')}
                  </span>
                  <span className="font-body text-sm text-warm-gray">
                    {t('category.guideSubtitle')}
                  </span>
                </div>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {regular.map((entity: any) => (
                  <EntityCard key={entity.handle} entity={entity} category={category || ''} />
                ))}
              </div>
            </div>
          )}

          {entities.length === 0 && (
            <div className="text-center py-20 text-warm-gray font-body">
              {t('category.emptyState')}
            </div>
          )}
        </Container>
      </div>
    </>
  );
}
